import { type SupabaseClient } from "@supabase/supabase-js";

import { type ClientMealsPlanDetails } from "@fitness-app/data-models/entities/ClientNutrition";
import { type ClientNutritionDay } from "@fitness-app/data-models/entities/ClientNutritionDay";
import { type Database } from "@fitness-app/supabase";
import { hasClientInputInNutritionDay } from "@fitness-app/utils/src/nutrition/hasClientInputInNutritionDay";
import {
  findSourcePlanDay,
  isUntouchedCopyOfPlanDay,
} from "@fitness-app/utils/src/nutrition/isUntouchedCopyOfPlanDay";

/**
 * Touching a dish on a future date persists that day with a snapshot of the plan active right
 * then; undoing the change leaves the snapshot behind with nothing logged in it. After a plan
 * swap those leftovers would keep serving the previous diet (TRE-1829) — drop them from the new
 * plan's start date onwards.
 *
 * A day only counts as a leftover when it is BOTH unlogged and still a verbatim copy of the plan
 * day it was snapshotted from. Judging by the tracking state alone deleted the weeks clients had
 * arranged in advance by swapping dishes, since a swap resets the incoming dish to `status: EMPTY`
 * (TRE-2033). Best-effort: the caller has already swapped the plan and must not fail because of
 * this.
 */
export const dropUntouchedTrackedDays = async (
  db: SupabaseClient<Database>,
  { traineeId, currentMealsPlanId, fromDate }: { traineeId: string; currentMealsPlanId: string; fromDate: string },
): Promise<void> => {
  const { data } = await db
    .from("client_nutrition_day")
    .select("id, day, updatedAt, clientMealsPlanId")
    .eq("traineeId", traineeId)
    .gte("eventDate", fromDate)
    .not("clientMealsPlanId", "is", null)
    .neq("clientMealsPlanId", currentMealsPlanId)
    .returns<Pick<ClientNutritionDay, "id" | "day" | "updatedAt" | "clientMealsPlanId">[]>();

  const candidates = (data || []).filter((day) => !hasClientInputInNutritionDay(day.day));

  if (!candidates.length) {
    return;
  }

  const planIds = [...new Set(candidates.map((day) => day.clientMealsPlanId).filter((id): id is string => !!id))];

  const { data: details } = await db
    .from("client_meals_plan_details")
    .select("planId, weeks")
    .in("planId", planIds)
    .returns<Pick<ClientMealsPlanDetails, "planId" | "weeks">[]>();

  const planDaysByPlan = new Map(
    (details || []).map((plan) => [plan.planId, (plan.weeks || []).flatMap((week) => week.days || [])] as const),
  );

  // A day whose source plan day cannot be found is left alone: the plan details are gone or the
  // snapshot predates them, and an unanswerable question is not a licence to delete.
  const stale = candidates.filter((day) => {
    const planDays = day.clientMealsPlanId ? planDaysByPlan.get(day.clientMealsPlanId) : undefined;

    return isUntouchedCopyOfPlanDay(day.day, findSourcePlanDay(day.day, planDays || []));
  });

  // Deleted one by one, matched on the `updatedAt` we read: the emptiness check runs here rather
  // than in the query, so a client logging a meal between the read and the delete would otherwise
  // lose it. A row touched in the meantime no longer matches and is left alone.
  // The matching `client_nutrition_week` summaries are dropped by the `client_nutrition_day`
  // DELETE webhook.
  await Promise.all(
    stale.map((day) => db.from("client_nutrition_day").delete().eq("id", day.id).eq("updatedAt", day.updatedAt)),
  );
};
